import { FormSection } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import React from "react";
import ReadonlyArtist from "show/components/ArtistAttendeePicker/ReadonlyArtist";
import ReadonlyAttendees from "show/components/ArtistAttendeePicker/ReadonlyAttendees";
import { getInstructionForInternal, ShowFieldMetaData, ShowFormState } from "shared/utils/fieldsHelper/showFields";
import AttendeePicker from "./AttendeePicker";

export const artistSection = (
  show: server.dto.Show,
  showInfo: ShowFormState,
  isExternal: boolean,
  fieldMetaData: ShowFieldMetaData,
  isSidebar: boolean
): FormSection => {
  const isInternalUser = !isExternal;

  const section: FormSection = {
    type: "regular",
    key: "showArtistSection",
    title: site.currentEnvironment.functionGroups.artist.singular,
    hidden: !site.currentEnvironment.functionGroups.artist.enabled,
    fields: [{
      ...fieldMetaData.artist.fields(),
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      render: () => <ReadonlyArtist artist={showInfo.artist} displayTooltip={isInternalUser} />,
      inPageEdit: {
        hoverEdit: true,
        // If a user doesn't have access to all artists, then removing an artist would prevent them from accessing the show
        canRemove: site.permissions.canAccessAllArtists,
        initialValue: showInfo.artist,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <ArtistModalPicker
              securityScope="limitByUserAccess"
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.ArtistSmall) => {
          let attendeeContactIds: number[] = [];
          if (value?.artistId) {
            const contactsRes = await window.CoreApi.CompanyToContact.ListContactByCompany({
              companyId: value.artistId,
              defaultSelect: true,
              isActive: true,
              page: 1,
              perPage: 1000,
            });
            if (!contactsRes.data.hasErrors) {
              attendeeContactIds = contactsRes.data.data.map(c => c.contact.contactId);
            }
          }
          return window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              artistId: value?.artistId ?? null,
              attendees: attendeeContactIds,
            })
          );
        }
      }
    }, {
      ...fieldMetaData.attendeeSection.fields(),
      key: "attendees",
      label: RESX.Planning.Attendees,
      render: () => (
        <div className="flex-column gap-small">
          <ReadonlyAttendees
            hasArtist={showInfo.artist != null}
            attendees={showInfo.attendees}
            withDetailViewLink={isInternalUser}
          />
        </div>
      ),
      inPageEdit: {
        canRemove: true,
        initialValue: showInfo.attendees,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <div className="main-section-template-fields-padding">
            <AttendeePicker
              artistId={showInfo.artist.artistId}
              attendees={value}
              onAttendeesChange={onChange}
              canCreate={!isSidebar}
            />
          </div>
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.ContactSmallV2[]) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              attendees: value?.map(a => a.contactId) ?? [],
            })
          )
      }
    }]
  };

  return section;
};
